import { getDeliveryByEventLabel, getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type {
  BaseFieldObject,
  PlacementProductListFieldObject,
  Price,
} from '@noths/polaris-dev-ts-types';
import { deliveryByEventNames } from '@noths/polaris-dev-ts-types';

import type { Placement } from 'src/constants/recommendations';
import { getPersonalisationBreakdown } from 'src/redux/personalisation/helpers/selectorHelpers';
import type { SelectedPersonalisationsRecord } from 'src/redux/personalisation/types';
import type { ProductState } from 'src/redux/product/types';
import type { ProductFieldObject } from 'src/types/googleAnalytics';
import type { ListingsCarouselProduct } from '../adapters/listingsAPIToProductCarouselAdapter';
import { formatIntegerPriceToDecimal } from './price';
import { logger } from './serverLogger';

const getPriceInGBP = (prices: Price[], personalisationTotal?: number) => {
  const priceInGBP = prices.find(({ currency }) => currency === 'GBP');
  const personalisationCost = personalisationTotal || 0;

  const price = priceInGBP?.amount
    ? formatIntegerPriceToDecimal(priceInGBP.amount + personalisationCost)
    : '';

  return price;
};

export const generateProductFieldObject = ({
  product,
  quantity,
  selectedPersonalisations,
}: {
  product: Pick<
    ProductState,
    | 'code'
    | 'deliveryByEvent'
    | 'freeDomesticDelivery'
    | 'inventoryStatus'
    | 'isNew'
    | 'numberOfReviews'
    | 'onSale'
    | 'partner'
    | 'prices'
    | 'productRating'
    | 'purchasable'
    | 'salePercentage'
    | 'title'
  >;
  quantity?: number;
  selectedPersonalisations: SelectedPersonalisationsRecord;
}): ProductFieldObject => {
  const {
    code,
    deliveryByEvent,
    freeDomesticDelivery,
    inventoryStatus,
    isNew,
    numberOfReviews,
    onSale,
    partner,
    prices,
    productRating,
    purchasable,
    salePercentage,
    title,
  } = product;

  const dimension22List = [
    freeDomesticDelivery && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
    deliveryByEvent?.arriveByEvent &&
      deliveryByEventNames.includes(deliveryByEvent?.eventName) &&
      getDeliveryByEventLabel(deliveryByEvent.eventName).toLowerCase().replace(' for', ''),
  ]
    .filter(Boolean)
    .join(', ');

  const dimension92 = Object.values(selectedPersonalisations)
    .reduce((personalisationCount, item = []) => personalisationCount + item.length, 0)
    .toString();

  const { amountAdded: addedPersonalisationCost, amountRemoved: removedPersonalisationCost } =
    getPersonalisationBreakdown(selectedPersonalisations, 'GBP');

  const productPrice = getPriceInGBP(prices);
  const totalPrice = getPriceInGBP(prices, addedPersonalisationCost + removedPersonalisationCost);
  const productId = String(getProductIdFromProductCode(code));

  // Add console error check for pipe character in productId
  if (productId.includes('|')) {
    logger.warn(`Product ID contains pipe character: ${productId} (from code: ${code})`);
  }

  return {
    brand: partner.name,
    dimension22: dimension22List,
    dimension30: String(code),
    dimension32: productPrice,
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    dimension34: partner.shortcode,
    dimension89: inventoryStatus,
    dimension90: String(numberOfReviews),
    dimension91: String(productRating || ''),
    dimension92: dimension92,
    id: productId,
    name: title,
    price: totalPrice,
    quantity,
  };
};

interface GenerateProductsForRecommendationsEventParams {
  personalisationsTotalAmount?: number;
  placementName: Placement | string;
  placementStrategy: string;
  positionInList?: number;
  products: ListingsCarouselProduct[];
}

export const generateProductsForRecommendationsEvent = (
  {
    personalisationsTotalAmount = 0,
    placementName,
    placementStrategy,
    positionInList,
    products,
  }: GenerateProductsForRecommendationsEventParams,
  omitKeys: Array<keyof PlacementProductListFieldObject> = [],
): BaseFieldObject[] =>
  products.map((product, index) => {
    const dimension22List = [
      product.freeDomesticDelivery && 'free uk delivery',
      product.isNew && 'new',
      product.onSale && !!product.salePercentage && `-${product.salePercentage}% off`,
      !product.isPurchasable && 'out of stock',
    ]
      .filter(Boolean)
      .join(', ');

    const productPrice = getPriceInGBP(product.prices);
    const totalPrice = getPriceInGBP(product.prices, personalisationsTotalAmount);
    const position = positionInList || index + 1;
    const list: PlacementProductListFieldObject['list'] = placementStrategy
      ? `product - listings recommendations - ${placementName} - ${placementStrategy}`
      : `product - listings recommendations - ${placementName}`;

    const baseFieldObject: BaseFieldObject = {
      name: product.title,
      id: String(getProductIdFromProductCode(product.code)),
      dimension22: dimension22List,
      dimension25: position,
      dimension30: String(product.code),
      dimension31: productPrice,
      price: totalPrice,
      dimension32: productPrice,
      dimension33: product.onSale ? `${product.salePercentage}.0%` : 'none',
      brand: product.partner.name,
      dimension34: product.partner.shortcode,
      position: position,
      list,
      dimension16: 'not set',
    };

    if (omitKeys.length) {
      return Object.entries(baseFieldObject).reduce<Record<string, unknown>>(
        (acc, [key, value]) => {
          if (!omitKeys.includes(key as keyof PlacementProductListFieldObject)) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      ) as unknown as BaseFieldObject;
    }

    return baseFieldObject;
  });
